import ThemeProvider from '@maxpreps/shared/corecomponents/Styles/ThemeProvider';
import { FormikConfig } from 'formik';
import dynamic from 'next/dynamic';
import { useEffect, useRef } from 'react';
import styled, { useTheme } from 'styled-components';
import Portal, { IPortalProps } from '../../corecomponents/Portal/Portal';
import useHasMounted from '../../hooks/useHasMounted';
import { IChildrenProps } from '../../models/utilTypes';
import { DefaultDeviceWidth, MediaQuery } from '../../styles/breakpoint';
import THEME from '../../styles/colors';
import StyledCard from '../Card/StyledCard';

const FormikModalWrapper = dynamic(() => import('./FormikModalWrapper'), {
  ssr: false
});

const StyledOverlay = styled.div<{
  $maxWidth?: string;
  $minWidth?: string;
  $borderRadius?: string;
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  z-index: 10000000;
  background-color: ${props => props.theme.colors.shadow.medium};

  ${StyledCard}, form {
    position: relative;
    width: 100%;
    max-width: ${props => props.$maxWidth ?? '90%'};
    ${MediaQuery.Tablet} {
      max-width: ${props =>
        props.$maxWidth ?? `${DefaultDeviceWidth.Tablet}px`};
    }
    ${props => (props.$minWidth ? `min-width: ${props.$minWidth}` : '')};
    ${props =>
      props.$borderRadius ? `border-radius: ${props.$borderRadius}` : ''};
  }
`;

// prevent clicks on the modal from propagating to the background click
const stopPropagation = e => e.stopPropagation();

interface IModalProps extends IPortalProps, IChildrenProps {
  onOutClick: Function;
  portalId: string;
  minWidth?: string;
  maxWidth?: string;
  borderRadius?: string;
  formikProps?: FormikConfig<any>;
}

const Modal: React.FC<IModalProps> = ({
  onOutClick,
  portalId,
  element,
  minWidth,
  maxWidth,
  borderRadius,
  formikProps,
  children
}) => {
  const hasMounted = useHasMounted();
  const ref = useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const handleOutClick = e => {
      if (
        // Prevent click-and-drag from closing modal
        document.activeElement?.nodeName === 'INPUT' ||
        // Prevent inside clicks from closing modal
        ref.current?.contains(e.target)
      ) {
        return;
      }

      onOutClick(e);
    };

    document.addEventListener('click', handleOutClick, true);
    return () => document.removeEventListener('click', handleOutClick, true);
  }, [onOutClick]);

  if (!hasMounted) return null;

  const mergedTheme = {
    ...theme,
    colors: {
      ...THEME, // Always use light-mode
      school: theme.colors.school
    }
  };

  const modalCard = <StyledCard ref={ref}>{children}</StyledCard>;

  const overlayContent = formikProps ? (
    <FormikModalWrapper {...formikProps}>{modalCard}</FormikModalWrapper>
  ) : (
    modalCard
  );

  return (
    <ThemeProvider theme={mergedTheme}>
      <Portal portalId={portalId} element={element}>
        <StyledOverlay
          $maxWidth={maxWidth}
          $minWidth={minWidth}
          $borderRadius={borderRadius}
        >
          {overlayContent}
        </StyledOverlay>
      </Portal>
    </ThemeProvider>
  );
};

export default Modal;
